import React, { useState, useRef, useCallback } from 'react';
import { ProductImage, ProductImageCategory, ProductImageRemovalReason } from '../../../types/knowledge';
import knowledgeApiService from '../../../services/api/knowledge/knowledgeApiService';
import UrlImageSelectionModal from '../../product/UrlImageSelectionModal';
import { ProductImage as ExtractedProductImage } from '../../../services/api/productImageService';
import { Upload, Trash2, Star, Edit2, X, Check, GripVertical, Image as ImageIcon, Link } from 'lucide-react';
import { useConfirm } from '../../tasky/modal';
import { TaskyButton } from '../../tasky/button';
import { useToast } from '../../../contexts/ToastContext';

interface ProductImagesSectionProps {
  productId: number;
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
}

interface CategoryConfig {
  key: ProductImageCategory;
  title: string;
  buttonText: string;
  emptyText: string;
  /** ドットのグラデーションと空状態アイコンに使う実色（CSS 以外でも参照するため hex のまま保持） */
  color: string;
  /** 件数バッジの配色（旧 $tagBg / $tagColor 相当） */
  countClass: string;
  /** アップロードボタンの配色（旧 $color / $hoverColor 相当） */
  uploadButtonClass: string;
  /** URL追加ボタンの配色（旧 $color / $hoverColor 相当） */
  urlButtonClass: string;
}

const CATEGORY_CONFIGS: CategoryConfig[] = [
  {
    key: 'brand_logo',
    title: 'ブランドロゴ',
    buttonText: 'ロゴを追加',
    emptyText: 'ブランドロゴが登録されていません',
    color: '#3B82F6',
    countClass: 'bg-blue-100 text-blue-700',
    uploadButtonClass: 'bg-blue-500 enabled:hover:bg-blue-600',
    urlButtonClass: 'text-blue-500 border-blue-500 enabled:hover:bg-blue-500 enabled:hover:text-white',
  },
  {
    key: 'product_image',
    title: '商品/サービス画像',
    buttonText: '画像を追加',
    emptyText: '商品/サービス画像が登録されていません',
    color: '#22C55E',
    countClass: 'bg-green-100 text-green-700',
    uploadButtonClass: 'bg-green-500 enabled:hover:bg-green-600',
    urlButtonClass: 'text-green-500 border-green-500 enabled:hover:bg-green-500 enabled:hover:text-white',
  },
  {
    key: 'material',
    title: 'イメージ素材',
    buttonText: '素材を追加',
    emptyText: 'イメージ素材が登録されていません',
    color: '#A855F7',
    countClass: 'bg-purple-100 text-purple-700',
    uploadButtonClass: 'bg-purple-500 enabled:hover:bg-purple-600',
    urlButtonClass: 'text-purple-500 border-purple-500 enabled:hover:bg-purple-500 enabled:hover:text-white',
  },
];

const REMOVAL_REASON_LABELS: Record<ProductImageRemovalReason, string> = {
  icon: 'アイコン',
  other_brand: '他社のロゴ',
  unrelated: '商品と関係のない画像',
  other_product: '別の商品',
};

/**
 * Web ページの URL を画像 URL 欄に貼ったときだけバックエンドが返す reason。
 * このときに限り「ページ内から画像を探す」へ回せる（PDF や 403 では無意味）。
 */
const REASON_HTML_PAGE = 'html_page';

/**
 * 画像抽出モーダルは productImageService の ProductImage を返す。
 * knowledge 側の型は title/description が undefined 許容・url 必須なので橋渡しする。
 */
const toKnowledgeImage = (image: ExtractedProductImage): ProductImage => ({
  ...image,
  title: image.title ?? undefined,
  description: image.description ?? undefined,
  url: image.url ?? image.signed_url ?? '',
});

const ProductImagesSection: React.FC<ProductImagesSectionProps> = ({
  productId,
  images,
  onImagesChange,
}) => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState<ProductImageCategory | null>(null);
  const [editingImageId, setEditingImageId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ title: string; description: string }>({
    title: '',
    description: '',
  });
  const [draggedImageId, setDraggedImageId] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<ProductImageCategory, HTMLInputElement | null>>({
    brand_logo: null,
    product_image: null,
    material: null,
  });

  // URL upload state
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlModalCategory, setUrlModalCategory] = useState<ProductImageCategory>('material');
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [urlDescription, setUrlDescription] = useState('');
  const [uploadingUrl, setUploadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  // 失敗の機械可読な分類。文言ではなくこれで復帰導線を出し分ける。
  const [urlErrorReason, setUrlErrorReason] = useState<string | null>(null);

  // 「このページから画像を探す」で開く抽出モーダル
  const [extractUrl, setExtractUrl] = useState<string | null>(null);

  const getImagesByCategory = useCallback((category: ProductImageCategory) => {
    return images.filter((img) => img.category === category && !img.removal_suggestion);
  }, [images]);

  const handleFileSelect = useCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
    category: ProductImageCategory
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingCategory(category);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        return knowledgeApiService.uploadProductImage(productId, file, category);
      });

      const newImages = await Promise.all(uploadPromises);
      onImagesChange([...images, ...newImages]);
    } catch (error) {
      console.error('Failed to upload images:', error);
      showToast('画像のアップロードに失敗しました', 'error');
    } finally {
      setUploadingCategory(null);
      const inputRef = fileInputRefs.current[category];
      if (inputRef) {
        inputRef.value = '';
      }
    }
  }, [productId, images, onImagesChange, showToast]);

  const openUrlModal = useCallback((category: ProductImageCategory) => {
    setUrlModalCategory(category);
    setUrlInput('');
    setUrlTitle('');
    setUrlDescription('');
    setUrlError(null);
    setUrlErrorReason(null);
    setShowUrlModal(true);
  }, []);

  const closeUrlModal = useCallback(() => {
    setShowUrlModal(false);
    setUrlInput('');
    setUrlTitle('');
    setUrlDescription('');
    setUrlError(null);
    setUrlErrorReason(null);
  }, []);

  const handleUrlUpload = useCallback(async () => {
    if (!urlInput.trim()) {
      setUrlError('URLを入力してください');
      setUrlErrorReason(null);
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      setUrlError('有効なURLを入力してください');
      setUrlErrorReason(null);
      return;
    }

    setUploadingUrl(true);
    setUrlError(null);
    setUrlErrorReason(null);

    try {
      const newImage = await knowledgeApiService.uploadProductImageFromUrl(
        productId,
        urlInput.trim(),
        urlModalCategory,
        urlTitle.trim() || undefined,
        urlDescription.trim() || undefined
      );
      onImagesChange([...images, newImage]);
      closeUrlModal();
    } catch (error: any) {
      console.error('Failed to upload image from URL:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.url?.[0] || '画像のアップロードに失敗しました';
      setUrlError(errorMessage);
      setUrlErrorReason(error.response?.data?.reason ?? null);
    } finally {
      setUploadingUrl(false);
    }
  }, [productId, urlInput, urlModalCategory, urlTitle, urlDescription, images, onImagesChange, closeUrlModal]);

  /** 画像URLではなくページURLだったとき、そのページから画像を探しに行く */
  const handleExtractFromPage = useCallback(() => {
    const target = urlInput.trim();
    closeUrlModal();
    setExtractUrl(target);
  }, [urlInput, closeUrlModal]);

  const closeExtractModal = useCallback(() => {
    setExtractUrl(null);
  }, []);

  const handleExtractComplete = useCallback((savedImages: ExtractedProductImage[]) => {
    onImagesChange([...images, ...savedImages.map(toKnowledgeImage)]);
    setExtractUrl(null);
  }, [images, onImagesChange]);

  /** URL から取れなかったぶんを、同じカテゴリへのファイル選択に引き継ぐ */
  const handleFallbackUpload = useCallback(() => {
    const category = urlModalCategory;
    closeUrlModal();
    // ブラウザはユーザー操作の文脈でしかファイル選択を開かないため、
    // setState のあとでも同じクリックハンドラ内で同期的に呼ぶ。
    fileInputRefs.current[category]?.click();
  }, [urlModalCategory, closeUrlModal]);

  const handleDelete = useCallback(async (imageId: number) => {
    if (!(await confirm({ title: 'この画像を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;

    setLoading(true);
    try {
      await knowledgeApiService.deleteProductImage(productId, imageId);
      onImagesChange(images.filter((img) => img.id !== imageId));
    } catch (error) {
      console.error('Failed to delete image:', error);
      showToast('画像の削除に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  }, [productId, images, onImagesChange, confirm, showToast]);

  const handleKeep = useCallback(async (imageId: number) => {
    setLoading(true);
    try {
      await knowledgeApiService.updateProductImage(productId, imageId, { removal_suggestion: null });
      onImagesChange(images.map((img) => (img.id === imageId ? { ...img, removal_suggestion: null } : img)));
    } catch (error) {
      console.error('Failed to keep image:', error);
      showToast('画像を残せませんでした', 'error');
    } finally {
      setLoading(false);
    }
  }, [productId, images, onImagesChange, showToast]);

  const handleDeleteCandidates = useCallback(async () => {
    const candidates = images.filter((img) => img.removal_suggestion);
    if (!(await confirm({ title: `削除候補の${candidates.length}枚を削除しますか？`, tone: 'destructive', confirmText: '削除する' }))) return;

    setLoading(true);
    const deleted = new Set<number>();
    try {
      // 削除はプライマリの付け替えを伴うので 1 枚ずつ
      for (const image of candidates) {
        await knowledgeApiService.deleteProductImage(productId, image.id);
        deleted.add(image.id);
      }
    } catch (error) {
      console.error('Failed to delete candidate images:', error);
      showToast('削除できなかった画像があります', 'error');
    } finally {
      onImagesChange(images.filter((img) => !deleted.has(img.id)));
      setLoading(false);
    }
  }, [productId, images, onImagesChange, confirm, showToast]);

  const handleSetPrimary = useCallback(async (imageId: number) => {
    setLoading(true);
    try {
      await knowledgeApiService.setProductImagePrimary(productId, imageId);
      const updatedImages = images.map((img) => ({
        ...img,
        is_primary: img.id === imageId,
      }));
      onImagesChange(updatedImages);
    } catch (error) {
      console.error('Failed to set primary image:', error);
      showToast('プライマリ画像の設定に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  }, [productId, images, onImagesChange, showToast]);

  const startEdit = useCallback((image: ProductImage) => {
    setEditingImageId(image.id);
    setEditForm({
      title: image.title || '',
      description: image.description || '',
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingImageId(null);
    setEditForm({ title: '', description: '' });
  }, []);

  const saveEdit = useCallback(async () => {
    if (editingImageId === null) return;

    setLoading(true);
    try {
      const updatedImage = await knowledgeApiService.updateProductImage(
        productId,
        editingImageId,
        {
          title: editForm.title || undefined,
          description: editForm.description || undefined,
        }
      );
      const updatedImages = images.map((img) =>
        img.id === editingImageId ? updatedImage : img
      );
      onImagesChange(updatedImages);
      cancelEdit();
    } catch (error) {
      console.error('Failed to update image:', error);
      showToast('画像情報の更新に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  }, [productId, editingImageId, editForm, images, onImagesChange, cancelEdit, showToast]);

  const handleDragStart = useCallback((e: React.DragEvent, imageId: number) => {
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetImageId: number, category: ProductImageCategory) => {
    e.preventDefault();
    if (draggedImageId === null || draggedImageId === targetImageId) {
      setDraggedImageId(null);
      return;
    }

    const categoryImages = getImagesByCategory(category);
    const draggedIndex = categoryImages.findIndex((img) => img.id === draggedImageId);
    const targetIndex = categoryImages.findIndex((img) => img.id === targetImageId);

    if (draggedIndex === -1) {
      setDraggedImageId(null);
      return;
    }

    const newCategoryImages = [...categoryImages];
    const [draggedImage] = newCategoryImages.splice(draggedIndex, 1);
    newCategoryImages.splice(targetIndex, 0, draggedImage);

    const updatedImages = images.map((img) => {
      if (img.category !== category) return img;
      const newIndex = newCategoryImages.findIndex((catImg) => catImg.id === img.id);
      return { ...img, sort_order: newIndex };
    });

    onImagesChange(updatedImages);

    try {
      await knowledgeApiService.reorderProductImages(
        productId,
        newCategoryImages.map((img) => img.id)
      );
    } catch (error) {
      console.error('Failed to reorder images:', error);
    }

    setDraggedImageId(null);
  }, [draggedImageId, images, productId, onImagesChange, getImagesByCategory]);

  const handleDragEnd = useCallback(() => {
    setDraggedImageId(null);
  }, []);

  const renderImageCard = (image: ProductImage, config: CategoryConfig) => (
    <div
      key={image.id}
      draggable
      onDragStart={(e) => handleDragStart(e, image.id)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, image.id, config.key)}
      onDragEnd={handleDragEnd}
      className={`${IMAGE_CARD} ${draggedImageId === image.id ? 'opacity-50' : 'opacity-100'}`}
    >
      <div className={DRAG_HANDLE}>
        <GripVertical size={16} />
      </div>

      <div className={IMAGE_WRAPPER}>
        <img className={PRODUCT_IMAGE_ELEMENT} src={image.url} alt={image.title || image.file_name} />
        {image.is_primary && (
          <div className={PRIMARY_BADGE}>
            <Star size={12} fill="currentColor" />
            プライマリ
          </div>
        )}
      </div>

      {editingImageId === image.id ? (
        <div className={EDIT_FORM}>
          <input
            className={EDIT_INPUT}
            type="text"
            placeholder="タイトル"
            value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          />
          <textarea
            className={EDIT_TEXTAREA}
            placeholder="説明"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            rows={2}
          />
          <div className={EDIT_ACTIONS}>
            <IconButton onClick={saveEdit} disabled={loading} variant="success">
              <Check size={14} />
            </IconButton>
            <IconButton onClick={cancelEdit} disabled={loading} variant="default">
              <X size={14} />
            </IconButton>
          </div>
        </div>
      ) : (
        <div className={IMAGE_INFO}>
          <h4 className={IMAGE_TITLE}>{image.title || '無題'}</h4>
          {image.description && (
            <p className={IMAGE_DESCRIPTION}>{image.description}</p>
          )}
          <p className={IMAGE_META}>
            {image.file_name} ({Math.round((image.file_size || 0) / 1024)} KB)
          </p>
        </div>
      )}

      <div className={CARD_ACTIONS}>
        {!image.is_primary && (
          <IconButton
            onClick={() => handleSetPrimary(image.id)}
            disabled={loading}
            title="プライマリに設定"
          >
            <Star size={14} />
          </IconButton>
        )}
        <IconButton
          onClick={() => startEdit(image)}
          disabled={loading || editingImageId !== null}
          title="編集"
        >
          <Edit2 size={14} />
        </IconButton>
        <IconButton
          onClick={() => handleDelete(image.id)}
          disabled={loading}
          title="削除"
          variant="danger"
        >
          <Trash2 size={14} />
        </IconButton>
      </div>
    </div>
  );

  const renderCategorySection = (config: CategoryConfig) => {
    const categoryImages = getImagesByCategory(config.key);
    const isUploading = uploadingCategory === config.key;

    return (
      <div className={CATEGORY_SECTION} key={config.key}>
        <div className={CATEGORY_HEADER}>
          <div className={CATEGORY_TITLE_WRAPPER}>
            <div
              className={CATEGORY_DOT}
              style={{ background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)` }}
            />
            <h4 className={CATEGORY_TITLE}>{config.title}</h4>
            <span className={`${CATEGORY_COUNT} ${config.countClass}`}>
              {categoryImages.length}
            </span>
          </div>
          <div className={CATEGORY_BUTTON_GROUP}>
            <button
              className={`${CATEGORY_UPLOAD_BUTTON} ${config.uploadButtonClass}`}
              onClick={() => fileInputRefs.current[config.key]?.click()}
              disabled={isUploading}
            >
              <Upload size={16} />
              {isUploading ? 'アップロード中...' : config.buttonText}
            </button>
            <button
              className={`${CATEGORY_URL_BUTTON} ${config.urlButtonClass}`}
              onClick={() => openUrlModal(config.key)}
              disabled={isUploading || uploadingUrl}
            >
              <Link size={16} />
              URLから追加
            </button>
          </div>
          <input
            ref={(el) => { fileInputRefs.current[config.key] = el; }}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFileSelect(e, config.key)}
          />
        </div>

        {categoryImages.length === 0 ? (
          <div className={CATEGORY_EMPTY_STATE}>
            <ImageIcon size={36} color={config.color} />
            <p className={CATEGORY_EMPTY_TEXT}>{config.emptyText}</p>
          </div>
        ) : (
          <div className={IMAGES_GRID}>
            {categoryImages.map((image) => renderImageCard(image, config))}
          </div>
        )}
      </div>
    );
  };

  const renderRemovalCandidates = () => {
    const candidates = images.filter((img) => img.removal_suggestion);
    if (candidates.length === 0) return null;

    return (
      <section className={CANDIDATE_SECTION} aria-label="削除候補">
        <div className={CATEGORY_HEADER}>
          <div className={CATEGORY_TITLE_WRAPPER}>
            <h4 className={CATEGORY_TITLE}>削除候補</h4>
            <span className={`${CATEGORY_COUNT} bg-gray-100 text-gray-600`}>{candidates.length}</span>
          </div>
          <TaskyButton variant="destructive" size="sm" onClick={handleDeleteCandidates} disabled={loading}>
            <Trash2 />
            まとめて削除
          </TaskyButton>
        </div>
        <p className={CANDIDATE_NOTE}>バナーには、ほかの画像を先に使います。</p>
        <div className={IMAGES_GRID}>
          {candidates.map((image) => (
            <div key={image.id} className={CANDIDATE_CARD}>
              <div className={IMAGE_WRAPPER}>
                <img className={PRODUCT_IMAGE_ELEMENT} src={image.url} alt={image.title || image.file_name} />
              </div>
              <div className={IMAGE_INFO}>
                <h4 className={IMAGE_TITLE}>{REMOVAL_REASON_LABELS[image.removal_suggestion as ProductImageRemovalReason] ?? '削除候補'}</h4>
                <p className={IMAGE_META}>{image.file_name}</p>
              </div>
              <div className={CARD_ACTIONS}>
                <TaskyButton variant="secondary" size="sm" className="h-8" onClick={() => handleKeep(image.id)} disabled={loading}>
                  <Check />
                  残す
                </TaskyButton>
                <IconButton onClick={() => handleDelete(image.id)} disabled={loading} title="削除" variant="danger">
                  <Trash2 size={14} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className={CONTAINER}>
      {CATEGORY_CONFIGS.map(renderCategorySection)}

      {renderRemovalCandidates()}

      {/* URL Upload Modal */}
      {showUrlModal && (
        <div className={URL_MODAL_OVERLAY} onClick={closeUrlModal}>
          <div className={URL_MODAL_CONTENT} onClick={(e) => e.stopPropagation()}>
            <div className={URL_MODAL_HEADER}>
              <h3 className={URL_MODAL_TITLE}>
                <Link size={20} />
                URLから画像を追加
              </h3>
              <IconButton onClick={closeUrlModal}>
                <X size={18} />
              </IconButton>
            </div>

            <div className={URL_MODAL_BODY}>
              <div className={URL_INPUT_GROUP}>
                <label className={URL_LABEL}>画像URL <span className={URL_REQUIRED}>*</span></label>
                <input
                  className={URL_INPUT}
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  disabled={uploadingUrl}
                />
              </div>

              <div className={URL_INPUT_GROUP}>
                <label className={URL_LABEL}>タイトル</label>
                <input
                  className={URL_INPUT}
                  type="text"
                  placeholder="画像のタイトル（任意）"
                  value={urlTitle}
                  onChange={(e) => setUrlTitle(e.target.value)}
                  disabled={uploadingUrl}
                />
              </div>

              <div className={URL_INPUT_GROUP}>
                <label className={URL_LABEL}>説明</label>
                <textarea
                  className={URL_TEXTAREA}
                  placeholder="画像の説明（任意）"
                  value={urlDescription}
                  onChange={(e) => setUrlDescription(e.target.value)}
                  disabled={uploadingUrl}
                  rows={3}
                />
              </div>

              {urlError && (
                <div className={URL_ERROR_MESSAGE}>{urlError}</div>
              )}

              {urlError && (
                <div className={URL_FALLBACK_ACTIONS}>
                  {urlErrorReason === REASON_HTML_PAGE && (
                    <button
                      type="button"
                      className={URL_FALLBACK_PRIMARY_BUTTON}
                      data-track="url-image-extract-from-page"
                      onClick={handleExtractFromPage}
                      disabled={uploadingUrl}
                    >
                      <ImageIcon size={16} />
                      このページから画像を探す
                    </button>
                  )}
                  <button
                    type="button"
                    className={URL_FALLBACK_SECONDARY_BUTTON}
                    data-track="url-image-fallback-upload"
                    onClick={handleFallbackUpload}
                    disabled={uploadingUrl}
                  >
                    <Upload size={16} />
                    代わりにファイルをアップロード
                  </button>
                </div>
              )}
            </div>

            <div className={URL_MODAL_FOOTER}>
              <button className={URL_CANCEL_BUTTON} onClick={closeUrlModal} disabled={uploadingUrl}>
                キャンセル
              </button>
              <button className={URL_SUBMIT_BUTTON} onClick={handleUrlUpload} disabled={uploadingUrl || !urlInput.trim()}>
                {uploadingUrl ? 'アップロード中...' : '追加する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        画像URLではなくページURLだったときの救済: ページ内の画像から選ばせる。
        閉じている間はマウントしない。ProductImagesTab.tsx が :last-child や
        :has() の構造セレクタでこのツリーを上書きしているため、常設の空ノードを
        置くとカテゴリ最終ブロックの余白が変わる。
      */}
      {extractUrl !== null && (
        <UrlImageSelectionModal
          isOpen
          onClose={closeExtractModal}
          url={extractUrl}
          productId={productId}
          onComplete={handleExtractComplete}
          onSkip={closeExtractModal}
        />
      )}
    </div>
  );
};

// === styled-components から移行した Tailwind クラス定義 ===
// DOM 構造（タグ種別・ネスト・兄弟順）は一切変更していない。
// ProductImagesTab.tsx が構造セレクタでこのツリーを上書きしているため、
// 要素の追加・削除・並べ替えは禁止。削除候補の <section> は file input を持たないのでセレクタに当たらない。

const CONTAINER = 'bg-transparent';

const CATEGORY_SECTION =
  'mb-5 p-[18px] border border-[#E5E1D8] rounded-lg bg-white last:mb-0';

const CATEGORY_HEADER = 'flex items-center justify-between gap-4 mb-4 flex-wrap';

const CATEGORY_TITLE_WRAPPER = 'flex items-center gap-3';

const CATEGORY_DOT = 'w-3 h-3 rounded-full';

const CATEGORY_TITLE = 'text-[18px] font-bold text-dark m-0';

const CATEGORY_COUNT = 'px-[10px] py-[2px] text-[12px] font-bold rounded-xl';

const CATEGORY_BUTTON_GROUP = 'flex gap-2 flex-wrap';

const CATEGORY_UPLOAD_BUTTON =
  'flex items-center gap-2 px-[14px] py-2 text-white border-0 rounded-lg text-[13px] font-bold cursor-pointer transition-all duration-200 ease-[ease] disabled:opacity-60 disabled:cursor-not-allowed';

const CATEGORY_URL_BUTTON =
  'flex items-center gap-2 px-[14px] py-2 bg-white border rounded-lg text-[13px] font-bold cursor-pointer transition-all duration-200 ease-[ease] disabled:opacity-60 disabled:cursor-not-allowed';

const CATEGORY_EMPTY_STATE =
  'flex flex-col items-center justify-center p-12 bg-[#F8F6F2] rounded-lg border border-dashed border-[#D8D2C5]';

const CATEGORY_EMPTY_TEXT = 'm-0 mt-4 text-[14px] text-gray-500';

const IMAGES_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-[14px]';

const IMAGE_CARD =
  'relative bg-white border border-[#E5E1D8] rounded-lg overflow-hidden cursor-grab transition-all duration-200 ease-[ease] hover:border-[#CFC8BA] hover:shadow-[0_10px_22px_rgba(29,25,39,0.08)] active:cursor-grabbing';

const DRAG_HANDLE =
  'absolute top-2 left-2 z-[2] text-white bg-black/50 p-1 rounded cursor-grab';

const IMAGE_WRAPPER = 'relative w-full aspect-[4/3] bg-[#F8F6F2]';

const PRODUCT_IMAGE_ELEMENT = 'w-full h-full object-contain bg-[#F8F6F2]';

const PRIMARY_BADGE =
  'absolute top-2 right-2 flex items-center gap-1 px-[10px] py-1 bg-[linear-gradient(135deg,#fbbf24,#f59e0b)] text-white text-[12px] font-medium rounded-md shadow-[0_2px_8px_rgba(251,191,36,0.4)]';

const IMAGE_INFO = 'p-3';

// 元の `line-clamp: 1` は -webkit-box を伴わない単独宣言だったため、
// display を書き換えてしまう line-clamp-1 ユーティリティは使わず arbitrary property で再現する。
const IMAGE_TITLE =
  'm-0 mb-1 text-[14px] font-bold text-dark [line-clamp:1] overflow-hidden text-ellipsis whitespace-nowrap';

// こちらは -webkit-box / -webkit-box-orient / overflow:hidden をすべて伴うため line-clamp-2 と等価。
const IMAGE_DESCRIPTION = 'm-0 mb-2 text-[13px] text-gray-500 leading-[1.4] line-clamp-2';

const IMAGE_META = 'm-0 text-[12px] text-gray-400';

const CARD_ACTIONS = 'flex gap-[6px] px-4 py-3 border-t border-t-[#EEEAE2] bg-[#FBFAF7]';

const CANDIDATE_SECTION = 'mt-6 pt-5 border-t border-gray-200';

const CANDIDATE_NOTE = 'm-0 -mt-2 mb-4 text-xs text-gray-500';

const CANDIDATE_CARD = 'relative bg-white border border-gray-200 rounded-xl overflow-hidden';

const ICON_BUTTON_BASE =
  'flex items-center justify-center w-8 h-8 border border-[#E5E1D8] rounded-lg bg-white cursor-pointer transition-all duration-200 ease-[ease] enabled:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed';

const ICON_BUTTON_VARIANTS: Record<'default' | 'danger' | 'success', string> = {
  default: 'text-gray-500 enabled:hover:border-gray-300',
  danger: 'text-red-500 enabled:hover:border-red-500',
  success: 'text-green-500 enabled:hover:border-green-500',
};

type IconButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'danger' | 'success';
};

const IconButton: React.FC<IconButtonProps> = ({ variant = 'default', children, ...rest }) => (
  <button className={`${ICON_BUTTON_BASE} ${ICON_BUTTON_VARIANTS[variant]}`} {...rest}>
    {children}
  </button>
);

const EDIT_FORM = 'p-4 flex flex-col gap-2';

const EDIT_INPUT =
  'w-full px-3 py-[10px] border border-gray-300 rounded-md text-[14px] focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]';

const EDIT_TEXTAREA =
  'w-full px-3 py-[10px] border border-gray-300 rounded-md text-[14px] resize-none focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]';

const EDIT_ACTIONS = 'flex justify-end gap-[6px]';

const URL_MODAL_OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]';

const URL_MODAL_CONTENT =
  'bg-white rounded-xl w-full max-w-[480px] shadow-[0_20px_60px_rgba(0,0,0,0.3)] overflow-hidden';

const URL_MODAL_HEADER =
  'flex items-center justify-between px-6 py-5 border-b border-b-gray-200';

const URL_MODAL_TITLE =
  'flex items-center gap-3 text-[18px] font-semibold text-gray-800 m-0';

const URL_MODAL_BODY = 'p-6 flex flex-col gap-5';

const URL_INPUT_GROUP = 'flex flex-col gap-2';

const URL_LABEL = 'text-[14px] font-medium text-gray-700';

const URL_REQUIRED = 'text-red-500';

const URL_INPUT =
  'w-full px-4 py-3 border border-gray-300 rounded-lg text-[14px] transition-all duration-200 ease-[ease] focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] disabled:bg-gray-50 disabled:cursor-not-allowed placeholder:text-gray-400';

const URL_TEXTAREA =
  'w-full px-4 py-3 border border-gray-300 rounded-lg text-[14px] resize-none transition-all duration-200 ease-[ease] focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] disabled:bg-gray-50 disabled:cursor-not-allowed placeholder:text-gray-400';

const URL_ERROR_MESSAGE =
  'px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-[14px]';

const URL_FALLBACK_ACTIONS = 'flex flex-col gap-2';

const URL_FALLBACK_BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 px-4 py-[10px] rounded-lg text-[14px] font-medium cursor-pointer transition-all duration-200 ease-[ease] disabled:opacity-60 disabled:cursor-not-allowed';

const URL_FALLBACK_PRIMARY_BUTTON =
  `${URL_FALLBACK_BUTTON_BASE} bg-blue-500 text-white border-0 enabled:hover:bg-blue-600`;

const URL_FALLBACK_SECONDARY_BUTTON =
  `${URL_FALLBACK_BUTTON_BASE} bg-white text-gray-500 border border-gray-300 enabled:hover:bg-gray-100`;

const URL_MODAL_FOOTER =
  'flex justify-end gap-3 px-6 py-4 border-t border-t-gray-200 bg-gray-50';

const URL_CANCEL_BUTTON =
  'px-5 py-[10px] bg-white text-gray-500 border border-gray-300 rounded-lg text-[14px] font-medium cursor-pointer transition-all duration-200 ease-[ease] enabled:hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed';

const URL_SUBMIT_BUTTON =
  'px-5 py-[10px] bg-blue-500 text-white border-0 rounded-lg text-[14px] font-medium cursor-pointer transition-all duration-200 ease-[ease] enabled:hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed';

export default ProductImagesSection;
